import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Save, ExternalLink, AlertCircle, CheckCircle, DollarSign, 
  Link as LinkIcon, Plus, Trash2, GripVertical, ToggleLeft, ToggleRight, Crown 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Predefined payment provider templates
const PAYMENT_TEMPLATES = [
  { name: 'PayPal', icon: 'paypal', placeholder: 'https://paypal.me/yourbusiness', color: '#0070ba' },
  { name: 'Stripe', icon: 'stripe', placeholder: 'https://buy.stripe.com/xxx', color: '#635bff' },
  { name: 'Square', icon: 'square', placeholder: 'https://square.link/xxx', color: '#006aff' },
  { name: 'Venmo', icon: 'venmo', placeholder: 'https://venmo.com/yourbusiness', color: '#008cff' },
  { name: 'Cash App', icon: 'cashapp', placeholder: 'https://cash.app/$yourbusiness', color: '#00d632' },
  { name: 'Interac e-Transfer', icon: 'interac', placeholder: 'Email for e-Transfer', color: '#ffcc00' },
  { name: 'Bank Transfer', icon: 'bank', placeholder: 'Bank transfer instructions or link', color: '#333333' },
  { name: 'Other', icon: 'credit-card', placeholder: 'Your payment link', color: '#6b7280' },
];

const AdminPaymentSettings = () => {
  const [settings, setSettings] = useState({
    payment_enabled: false,
    price_amount: 9.99,
    price_currency: 'CAD',
    plan_name: 'Premium',
    plan_duration: '1 Year',
    payment_methods: [],
    // Donation settings
    donation_enabled: false,
    donation_methods: [],
    // Free trial settings
    free_trial_enabled: true,
    free_trial_days: 30
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [showAddDonationMethod, setShowAddDonationMethod] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/settings/payment`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast({
        title: "Error",
        description: "Failed to load payment settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/settings/payment`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast({
          title: "Settings Saved",
          description: "Payment settings have been updated successfully",
        });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast({
        title: "Error",
        description: "Failed to save payment settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addPaymentMethod = (template) => {
    const newMethod = {
      id: Date.now().toString(),
      name: template.name,
      link: '',
      icon: template.icon,
      enabled: true,
      description: '',
      color: template.color
    };
    
    setSettings(prev => ({
      ...prev,
      payment_methods: [...prev.payment_methods, newMethod]
    }));
    setShowAddMethod(false);
  };

  const addDonationMethod = (template) => {
    const newMethod = {
      id: Date.now().toString(),
      name: template.name,
      link: '',
      icon: template.icon,
      enabled: true,
      description: '',
      color: template.color
    };
    
    setSettings(prev => ({
      ...prev,
      donation_methods: [...(prev.donation_methods || []), newMethod]
    }));
    setShowAddDonationMethod(false);
  };

  const updatePaymentMethod = (id, field, value) => {
    setSettings(prev => ({
      ...prev,
      payment_methods: prev.payment_methods.map(method =>
        method.id === id ? { ...method, [field]: value } : method
      )
    }));
  };

  const updateDonationMethod = (id, field, value) => {
    setSettings(prev => ({
      ...prev,
      donation_methods: (prev.donation_methods || []).map(method =>
        method.id === id ? { ...method, [field]: value } : method
      )
    }));
  };

  const removePaymentMethod = (id) => {
    setSettings(prev => ({
      ...prev,
      payment_methods: prev.payment_methods.filter(method => method.id !== id)
    }));
  };

  const removeDonationMethod = (id) => {
    setSettings(prev => ({
      ...prev,
      donation_methods: (prev.donation_methods || []).filter(method => method.id !== id)
    }));
  };

  const togglePaymentMethod = (id) => {
    setSettings(prev => ({
      ...prev,
      payment_methods: prev.payment_methods.map(method =>
        method.id === id ? { ...method, enabled: !method.enabled } : method
      )
    }));
  };

  const toggleDonationMethod = (id) => {
    setSettings(prev => ({
      ...prev,
      donation_methods: (prev.donation_methods || []).map(method =>
        method.id === id ? { ...method, enabled: !method.enabled } : method
      )
    }));
  };

  const getPaymentIcon = (iconName) => {
    // Return appropriate icon based on payment type
    const icons = {
      paypal: (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#0070ba">
          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.65h6.916c2.077 0 3.573.405 4.44 1.204.855.79 1.188 1.942.989 3.423-.02.152-.044.3-.072.449l-.004.023c-.555 3.01-2.481 4.48-5.719 4.48H9.677a.77.77 0 0 0-.757.65l-.918 5.788a.641.641 0 0 1-.633.74h-.293v1.51z"/>
        </svg>
      ),
      stripe: (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#635bff">
          <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
        </svg>
      ),
      square: (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#006aff">
          <path d="M4.5 0A4.5 4.5 0 0 0 0 4.5v15A4.5 4.5 0 0 0 4.5 24h15a4.5 4.5 0 0 0 4.5-4.5v-15A4.5 4.5 0 0 0 19.5 0h-15zm4.125 6h6.75c1.17 0 2.125.955 2.125 2.125v6.75c0 1.17-.955 2.125-2.125 2.125h-6.75A2.126 2.126 0 0 1 6.5 14.875v-6.75C6.5 6.955 7.455 6 8.625 6z"/>
        </svg>
      ),
      default: <CreditCard className="w-5 h-5" />
    };
    return icons[iconName] || icons.default;
  };

  const enabledMethodsCount = settings.payment_methods.filter(m => m.enabled && m.link).length;
  const enabledDonationMethodsCount = (settings.donation_methods || []).filter(m => m.enabled && m.link).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mint-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Settings</h1>
          <p className="text-gray-600">Configure your payment methods and pricing</p>
        </div>
      </div>

      {/* Status Card */}
      <Card className={settings.payment_enabled && enabledMethodsCount > 0 ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            {settings.payment_enabled && enabledMethodsCount > 0 ? (
              <>
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-800">Payments Active</p>
                  <p className="text-sm text-green-700">{enabledMethodsCount} payment method{enabledMethodsCount !== 1 ? 's' : ''} enabled</p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="w-6 h-6 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-800">Payments Not Ready</p>
                  <p className="text-sm text-amber-700">
                    {settings.payment_methods.length === 0 
                      ? 'Add at least one payment method' 
                      : enabledMethodsCount === 0 
                        ? 'Enable at least one payment method with a link'
                        : 'Turn on "Enable Payments" to start accepting'}
                  </p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Master Enable Switch */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-semibold text-lg">Enable Payments</Label>
              <p className="text-sm text-gray-500">Turn on to allow users to purchase premium</p>
            </div>
            <Switch
              checked={settings.payment_enabled}
              onCheckedChange={(checked) => handleChange('payment_enabled', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Payment Methods */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment Methods
          </CardTitle>
          <Button
            onClick={() => setShowAddMethod(true)}
            size="sm"
            className="bg-mint-500 hover:bg-mint-600"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Method
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.payment_methods.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No payment methods added yet</p>
              <p className="text-sm">Click "Add Method" to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {settings.payment_methods.map((method, index) => (
                <div 
                  key={method.id} 
                  className={`border rounded-lg p-4 ${method.enabled ? 'bg-white' : 'bg-gray-50 opacity-75'}`}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                      style={{ backgroundColor: method.color || '#6b7280' }}
                    >
                      {getPaymentIcon(method.icon)}
                    </div>
                    
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{method.name}</span>
                          {method.enabled && method.link && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Active</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => togglePaymentMethod(method.id)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title={method.enabled ? 'Disable' : 'Enable'}
                          >
                            {method.enabled ? (
                              <ToggleRight className="w-6 h-6 text-green-600" />
                            ) : (
                              <ToggleLeft className="w-6 h-6 text-gray-400" />
                            )}
                          </button>
                          <button
                            onClick={() => removePaymentMethod(method.id)}
                            className="p-1 hover:bg-red-100 rounded text-red-500"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Input
                          placeholder={PAYMENT_TEMPLATES.find(t => t.name === method.name)?.placeholder || 'Payment link or instructions'}
                          value={method.link}
                          onChange={(e) => updatePaymentMethod(method.id, 'link', e.target.value)}
                          className="font-mono text-sm"
                        />
                        <Input
                          placeholder="Description (optional) - e.g., 'Pay instantly with PayPal'"
                          value={method.description || ''}
                          onChange={(e) => updatePaymentMethod(method.id, 'description', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                      
                      {method.link && (
                        <a
                          href={method.link.startsWith('http') ? method.link : `https://${method.link}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Test link
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Method Modal */}
          {showAddMethod && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
                <h3 className="text-lg font-semibold mb-4">Add Payment Method</h3>
                <p className="text-sm text-gray-600 mb-4">Select a payment provider:</p>
                
                <div className="grid grid-cols-2 gap-3">
                  {PAYMENT_TEMPLATES.map((template) => (
                    <button
                      key={template.name}
                      onClick={() => addPaymentMethod(template)}
                      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors text-left"
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                        style={{ backgroundColor: template.color }}
                      >
                        {getPaymentIcon(template.icon)}
                      </div>
                      <span className="font-medium">{template.name}</span>
                    </button>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowAddMethod(false)}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pricing Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Pricing Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="plan_name">Plan Name</Label>
              <Input
                id="plan_name"
                placeholder="Premium"
                value={settings.plan_name}
                onChange={(e) => handleChange('plan_name', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="price_amount">Price</Label>
              <Input
                id="price_amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="9.99"
                value={settings.price_amount}
                onChange={(e) => handleChange('price_amount', parseFloat(e.target.value) || 0)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="price_currency">Currency</Label>
              <select
                id="price_currency"
                value={settings.price_currency}
                onChange={(e) => handleChange('price_currency', e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-mint-500"
              >
                <option value="CAD">CAD - Canadian Dollar</option>
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="INR">INR - Indian Rupee</option>
                <option value="AUD">AUD - Australian Dollar</option>
                <option value="PKR">PKR - Pakistani Rupee</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="plan_duration">Plan Duration</Label>
              <select
                id="plan_duration"
                value={settings.plan_duration || '1 Year'}
                onChange={(e) => handleChange('plan_duration', e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-mint-500"
              >
                <option value="1 Month">1 Month</option>
                <option value="3 Months">3 Months</option>
                <option value="6 Months">6 Months</option>
                <option value="1 Year">1 Year</option>
              </select>
            </div>
          </div>

          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Preview:</strong> Users will see "{settings.price_currency === 'CAD' ? 'CA$' : settings.price_currency === 'USD' ? '$' : settings.price_currency}{settings.price_amount} / {settings.plan_duration || '1 Year'}"
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Free Trial Settings */}
      <Card className="border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-blue-600" />
            Free Trial Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <div>
              <Label className="font-semibold text-lg">Enable Free Trial</Label>
              <p className="text-sm text-gray-500">New users automatically get free premium access</p>
            </div>
            <Switch
              checked={settings.free_trial_enabled ?? true}
              onCheckedChange={(checked) => handleChange('free_trial_enabled', checked)}
            />
          </div>

          {settings.free_trial_enabled && (
            <div className="space-y-2">
              <Label htmlFor="free_trial_days">Free Trial Duration (Days)</Label>
              <Input
                id="free_trial_days"
                type="number"
                min="1"
                max="365"
                placeholder="30"
                value={settings.free_trial_days || 30}
                onChange={(e) => handleChange('free_trial_days', parseInt(e.target.value) || 30)}
                className="max-w-[200px]"
              />
              <p className="text-xs text-gray-500">
                New users will get {settings.free_trial_days || 30} days of free premium access when they register.
                After the trial expires, they will need to pay ${settings.price_amount}/{settings.plan_duration || '1 Year'} to continue.
              </p>
            </div>
          )}

          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2">How Free Trial Works:</h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>User registers → Gets <strong>{settings.free_trial_days || 30} days</strong> FREE premium</li>
              <li>After trial expires → Auto-reverts to normal (free) user</li>
              <li>User can then pay <strong>${settings.price_amount}/{settings.plan_duration || '1 Year'}</strong> for premium</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Donation Settings */}
      <Card className="border-purple-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-purple-600" />
              Donation Payment Methods
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">Separate payment links for donation page</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Enable Donations</Label>
              <Switch
                checked={settings.donation_enabled || false}
                onCheckedChange={(checked) => handleChange('donation_enabled', checked)}
              />
            </div>
            <Button
              onClick={() => setShowAddDonationMethod(true)}
              size="sm"
              className="bg-purple-500 hover:bg-purple-600"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Method
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(!settings.donation_methods || settings.donation_methods.length === 0) ? (
            <div className="text-center py-8 text-gray-500">
              <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No donation methods added yet</p>
              <p className="text-sm">Click "Add Method" to add donation payment links</p>
            </div>
          ) : (
            <div className="space-y-3">
              {settings.donation_methods.map((method) => (
                <div 
                  key={method.id} 
                  className={`border rounded-lg p-4 ${method.enabled ? 'bg-white border-purple-200' : 'bg-gray-50 opacity-75'}`}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                      style={{ backgroundColor: method.color || '#6b7280' }}
                    >
                      {getPaymentIcon(method.icon)}
                    </div>
                    
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{method.name}</span>
                          {method.enabled && method.link && (
                            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Active</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleDonationMethod(method.id)}
                            className="text-gray-400 hover:text-gray-600"
                            title={method.enabled ? 'Disable' : 'Enable'}
                          >
                            {method.enabled ? (
                              <ToggleRight className="w-6 h-6 text-purple-500" />
                            ) : (
                              <ToggleLeft className="w-6 h-6" />
                            )}
                          </button>
                          <button
                            onClick={() => removeDonationMethod(method.id)}
                            className="text-gray-400 hover:text-red-500"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Input
                          placeholder={PAYMENT_TEMPLATES.find(t => t.name === method.name)?.placeholder || 'Donation link'}
                          value={method.link}
                          onChange={(e) => updateDonationMethod(method.id, 'link', e.target.value)}
                          className="font-mono text-sm"
                        />
                        <Input
                          placeholder="Description (optional) - e.g., 'Support our mission'"
                          value={method.description || ''}
                          onChange={(e) => updateDonationMethod(method.id, 'description', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                      
                      {method.link && (
                        <a
                          href={method.link.startsWith('http') ? method.link : `https://${method.link}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-purple-600 hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Test link
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Donation Method Modal */}
          {showAddDonationMethod && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
                <h3 className="text-lg font-semibold mb-4">Add Donation Payment Method</h3>
                <p className="text-sm text-gray-600 mb-4">Select a payment provider for donations:</p>
                
                <div className="grid grid-cols-2 gap-3">
                  {PAYMENT_TEMPLATES.map((template) => (
                    <button
                      key={template.name}
                      onClick={() => addDonationMethod(template)}
                      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-purple-50 transition-colors text-left"
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                        style={{ backgroundColor: template.color }}
                      >
                        {getPaymentIcon(template.icon)}
                      </div>
                      <span className="font-medium">{template.name}</span>
                    </button>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowAddDonationMethod(false)}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-gray-600">
          <div className="flex items-start gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-mint-100 text-mint-700 font-semibold text-xs">1</span>
            <p>Add your payment methods (PayPal, Stripe, Square, etc.) using the "Add Method" button</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-mint-100 text-mint-700 font-semibold text-xs">2</span>
            <p>Paste your payment links for each method</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-mint-100 text-mint-700 font-semibold text-xs">3</span>
            <p>Set your price and enable payments</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-mint-100 text-mint-700 font-semibold text-xs">4</span>
            <p>Users choose their preferred payment method and complete payment</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-mint-100 text-mint-700 font-semibold text-xs">5</span>
            <p><strong>Important:</strong> After payment, manually activate premium in Admin → Users</p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-mint-500 hover:bg-mint-600"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AdminPaymentSettings;
